// Shared brand-document kit. The standards established by The Final Tribute
// Prospectus live here so every upcoming client document (Commission Proposal,
// Invoice, Services Agreement, etc.) inherits them by construction:
//   - embedded fonts (Libre Baskerville + Herr Von Muellerhoff script)
//   - white ground, Ink text, Oxblood as the single accent (no em-dashes)
//   - emblem masthead, italic section headings with an oxblood rule
//   - tan note boxes with an oxblood accent bar
//   - centred script signature + title
//   - footer with the stacked lockup at the shared colophon width and
//     clickable contact (email / WhatsApp / website)
// Keep signatures/emblem/lockup as inline SVG with embedded fonts; the PDF
// exporter rasterises them so they print correctly.

import { LIBRE_BASKERVILLE_ITALIC_WOFF2, LIBRE_BASKERVILLE_REGULAR_WOFF2 } from "./lockupFonts";
import { HERR_VON_MUELLERHOFF_WOFF2 } from "./signatureFont";
import { buildEmblemSvg, buildLockupSvg, STACKED_COLOPHON_WIDTH } from "./logoAssets";

export const escapeHtml = (s) =>
  String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// A personalised field: shows the value, or a bracketed placeholder when blank.
export const fillField = (val, placeholder) => {
  const v = (val || "").trim();
  return `<span class="fill">${v ? escapeHtml(v) : `[ ${placeholder} ]`}</span>`;
};

export const DOC_FONT_FACE = `
    @font-face{font-family:'Libre Baskerville';font-style:normal;font-weight:400;src:url(data:font/woff2;base64,${LIBRE_BASKERVILLE_REGULAR_WOFF2}) format('woff2');}
    @font-face{font-family:'Libre Baskerville';font-style:italic;font-weight:400;src:url(data:font/woff2;base64,${LIBRE_BASKERVILLE_ITALIC_WOFF2}) format('woff2');}
    @font-face{font-family:'RQ Signature';font-style:normal;font-weight:400;src:url(data:font/woff2;base64,${HERR_VON_MUELLERHOFF_WOFF2}) format('woff2');}
  `;

export const DOC_BASE_CSS = `
:root{--ink:#1C1A17;--paper:#F4F1EA;--ox:#7A2E2E;--slate:#6E6A5E;--hair:#D9D2CB;--deep:#E3DCCE;--oxlight:#B0573F;}
*{box-sizing:border-box;}
body{margin:0;background:#FFFFFF;color:var(--ink);font-family:"Libre Baskerville",Georgia,serif;line-height:1.7;-webkit-font-smoothing:antialiased;}
.sheet{max-width:720px;margin:0 auto;padding:60px 56px 80px;background:#FFFFFF;}
.mast{text-align:center;padding-bottom:26px;border-bottom:0.75px solid var(--ox);margin-bottom:26px;}
.mast .emblem{width:78px;margin:0 auto 12px;}
.mast .emblem svg{display:block;width:100%;height:auto;}
.wm{font-style:italic;font-size:30px;}
.sub{font-variant:small-caps;letter-spacing:0.32em;font-size:11px;color:var(--ox);margin-top:8px;}
h2{font-size:20px;font-weight:400;font-style:italic;margin:30px 0 8px;padding-bottom:5px;border-bottom:0.75px solid var(--ox);}
p{margin:0 0 13px;font-size:15px;}
.fill{color:var(--ox);font-style:italic;font-size:0.94em;}
.note{color:var(--slate);font-style:italic;font-size:12.5px;background:var(--deep);border-left:3px solid var(--ox);padding:13px 16px;margin:0 0 18px;}
strong{font-weight:400;color:var(--ox);font-style:normal;}
em{font-style:italic;}
table{width:100%;border-collapse:collapse;margin:12px 0 18px;font-size:13.5px;}
th{text-align:left;font-style:italic;font-weight:400;color:var(--ox);border-bottom:1px solid var(--ox);padding:8px 10px;}
td{border-bottom:0.5px solid var(--hair);padding:8px 10px;vertical-align:top;}
td.r,th.r{text-align:right;}
.strongcell{color:var(--ox);}
.center{text-align:center;}
.foot{margin-top:40px;padding-top:20px;border-top:0.75px solid var(--ox);text-align:center;}
.foot .footlogo{width:${STACKED_COLOPHON_WIDTH}px;margin:0 auto 16px;}
.foot .footlogo svg{display:block;width:100%;height:auto;}
.foot .fline{font-variant:small-caps;letter-spacing:0.26em;font-size:10.5px;color:var(--ox);margin:0 0 6px;}
.foot .conf{font-style:italic;color:var(--slate);font-size:12px;margin:0;}
.foot .contact{font-size:13px;margin:0 0 12px;color:var(--ink);}
.foot .contact a{color:inherit;text-decoration:none;}
.foot .contact a:hover{color:var(--ox);}
.sig{text-align:center;}
.sig .sigmark{margin:2px 0 6px;}
.sig .sigmark svg{margin:0 auto;}
.sig .sigtitle{color:var(--ox);font-size:11px;letter-spacing:0.34em;text-transform:uppercase;margin:8px 0 0;}
@media print{body{background:#FFFFFF;} .sheet{box-shadow:none;padding:40px;}}
@media(max-width:680px){.sheet{padding:36px 22px 60px;}}
`;

// Emblem + wordmark + spaced small-cap line.
export function buildMasthead({ line = "BESPOKE LEGACY PRESERVATION" } = {}) {
  return `<div class="mast"><div class="emblem">${buildEmblemSvg("ink")}</div><div class="wm">The Raven&#8217;s Quill</div><div class="sub">${line}</div></div>`;
}

// Centred document title with an oxblood small-cap kicker beneath.
export function buildDocTitle({ title, kicker }) {
  return `<div class="center" style="margin-bottom:22px;"><div style="font-style:italic;font-size:26px;">${title}</div>${
    kicker ? `<div class="sub" style="color:var(--ox);margin-top:6px;">${kicker}</div>` : ""
  }</div>`;
}

// The author signature mark: name in the Herr Von Muellerhoff script with the
// house oxblood curve, drawn as one embedded-font SVG (rasterises identically).
export function buildSignatureSvg({ name = "Zukiswa Phoofolo" } = {}) {
  return `<svg viewBox="0 0 800 185" width="360" height="83" xmlns="http://www.w3.org/2000/svg" style="display:block;overflow:visible">
    <defs><style>@font-face{font-family:'RQ Signature';font-style:normal;font-weight:400;src:url(data:font/woff2;base64,${HERR_VON_MUELLERHOFF_WOFF2}) format('woff2');}</style></defs>
    <text x="400" y="120" text-anchor="middle" font-family="'RQ Signature',cursive" font-size="92" fill="#1C1A17">${escapeHtml(name)}</text>
    <path d="M110 150 C 300 170, 500 170, 690 146" stroke="#7A2E2E" stroke-width="2" fill="none" stroke-linecap="round"/>
  </svg>`;
}

// Closing line + centred signature + small-cap title.
export function buildSignatureBlock({
  closing = "With care for the life at its centre,",
  name = "Zukiswa Phoofolo",
  title = "Writer-Anthropologist",
} = {}) {
  return `<div class="sig" style="margin-top:24px;border-top:0.5px solid var(--hair);padding-top:18px;"><p style="margin-bottom:6px;">${closing}</p><div class="sigmark">${buildSignatureSvg(
    { name }
  )}</div><div class="sigtitle">${title}</div></div>`;
}

// Clickable house contact line: email (mailto), phone (WhatsApp), website.
export function buildContactLinks() {
  return `<a href="mailto:info@theravensquill.co.za">info@theravensquill.co.za</a> &#183; <a href="https://wa.me/27792818435" target="_blank" rel="noopener">+27 79 281 8435</a> &#183; <a href="https://www.theravensquill.co.za" target="_blank" rel="noopener">www.theravensquill.co.za</a>`;
}

// Footer: stacked lockup + contact + optional small-cap line + confidential note.
export function buildFooter({ fline, conf } = {}) {
  return `<div class="foot"><div class="footlogo">${buildLockupSvg("ink")}</div><p class="contact">${buildContactLinks()}</p>${
    fline ? `<p class="fline">${fline}</p>` : ""
  }${conf ? `<p class="conf">${conf}</p>` : ""}</div>`;
}

// Assemble a complete, self-contained brand document (fonts + base CSS + body).
export function wrapDocument({ title, extraCss = "", body }) {
  return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0"><title>${title}</title>
<style>${DOC_FONT_FACE}${DOC_BASE_CSS}${extraCss}</style></head><body><div class="sheet">${body}</div></body></html>`;
}

// Shared "enclosed writing sample" callout + dark call-to-action block, used by
// the client-facing prospectuses (Life Study, Final Tribute, Organisational).
export const SAMPLE_CTA_CSS = `
.sample{background:#FAF8F2;border:0.75px solid var(--hair);border-top:2px solid var(--ox);padding:18px 20px;margin:18px 0 6px;}
.sample .sampleh{font-size:15.5px;font-weight:400;font-style:italic;color:var(--ox);margin:0 0 6px;}
.sample .samplep{margin:0;font-size:14px;}
.sample a{color:var(--ox);text-decoration:none;}
.sample a:hover{text-decoration:underline;}
.cta{background:#1C1A17;color:#F4F1EA;padding:26px 28px;margin:28px 0 12px;text-align:center;}
.cta .ctah{font-style:italic;font-size:20px;margin-bottom:8px;}
.cta .ctap{color:#E6E0D4;font-size:14px;margin:0 0 12px;}
.cta .ctalines{font-size:15px;line-height:2;}
.cta .ctalines a{color:#F4F1EA;text-decoration:none;border-bottom:0.5px solid var(--ox);}
@media print{.cta{background:#1C1A17 !important;-webkit-print-color-adjust:exact;print-color-adjust:exact;}}
`;

export function buildSampleCallout({ heading = "A writing sample is enclosed", text }) {
  return `<div class="sample"><h3 class="sampleh">${heading}</h3><p class="samplep">${text}</p></div>`;
}

export function buildCta({ heading, text }) {
  return `<div class="cta"><div class="ctah">${heading}</div><p class="ctap">${text}</p><div class="ctalines"><a href="mailto:info@theravensquill.co.za">info@theravensquill.co.za</a><br><a href="https://wa.me/27792818435" target="_blank" rel="noopener">+27 79 281 8435</a></div></div>`;
}
